import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Home as HomeIcon, Heart, Power, ChevronLeft, ChevronRight, Music, Square,
  Volume2, VolumeX, Tv as TvIcon, SkipForward, SkipBack, Youtube,
} from "lucide-react";
import { fetchTv } from "@/lib/api";
import { getTvMemories, addTvMemory } from "@/lib/memories";
import { Modal } from "@/components/Modal";
import { AmbientMusic } from "@/components/AmbientMusic";
import { playStatic } from "@/lib/soundfx";
import ambient from "@/lib/ambientAudio";

const CATS = [
  { id: "cartoons", label: "CARTOONS", sub: "Cartoon Network · Pogo" },
  { id: "serials", label: "INDIAN SERIALS", sub: "Sony · DD" },
  { id: "dramas", label: "DRAMAS", sub: "Star Plus" },
  { id: "kids", label: "KIDS SHOWS", sub: "DD · Star" },
];
const catLabel = (id) => CATS.find((c) => c.id === id)?.label || id;

export default function LivingRoomTv() {
  const navigate = useNavigate();
  const [shows, setShows] = useState([]);
  const [memories, setMemories] = useState(getTvMemories());
  const [power, setPower] = useState(false);
  const [turningOn, setTurningOn] = useState(false);
  const [category, setCategory] = useState(null);
  const [index, setIndex] = useState(0);
  const [staticOn, setStaticOn] = useState(false);
  const [collection, setCollection] = useState(false);
  const [cardOpen, setCardOpen] = useState(false);
  const [watching, setWatching] = useState(false);

  // audio
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [volume, setVolume] = useState(0.8);
  const [progress, setProgress] = useState(0);

  useEffect(() => { fetchTv().then(setShows).catch(() => toast.error("TV failed to load")); }, []);
  useEffect(() => () => ambient.duck(false), []);

  const list = useMemo(() => shows.filter((s) => s.category === category), [shows, category]);
  const current = list[index] || null;

  // unlock memory whenever a poster is displayed
  useEffect(() => {
    if (power && current) {
      if (!memories.includes(current.id)) {
        setMemories(addTvMemory(current.id));
        toast.success(`📺 Memory discovered: ${current.name}`);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current?.id, power]);

  const stopSong = () => {
    const a = audioRef.current;
    if (a) { a.pause(); a.currentTime = 0; }
    setPlaying(false);
    setProgress(0);
    ambient.duck(false);
  };

  const flashStatic = (fn) => {
    playStatic();
    setStaticOn(true);
    setTimeout(() => { fn(); }, 120);
    setTimeout(() => setStaticOn(false), 480);
  };

  const powerOn = () => {
    setTurningOn(true);
    setStaticOn(true);
    setTimeout(() => { setTurningOn(false); setStaticOn(false); setPower(true); }, 900);
  };
  const powerOff = () => {
    stopSong();
    setWatching(false);
    setPower(false);
    setCategory(null);
    setIndex(0);
  };
  const togglePower = () => (power ? powerOff() : powerOn());

  const pickCategory = (id) => { stopSong(); setWatching(false); flashStatic(() => { setCategory(id); setIndex(0); }); };
  const backToChannels = () => { stopSong(); setWatching(false); flashStatic(() => { setCategory(null); setIndex(0); }); };

  const changeShow = (dir) => {
    if (!list.length) return;
    stopSong();
    setWatching(false);
    flashStatic(() => setIndex((i) => (i + dir + list.length) % list.length));
  };

  const watchOnTv = () => {
    if (!current?.youtube_id) { toast("No video for this show yet"); return; }
    stopSong();
    flashStatic(() => setWatching(true));
  };
  const stopWatch = () => flashStatic(() => setWatching(false));

  // JINGLE = play the YouTube title song on the TV (fallback to the audio mp3 if no video)
  const hasJingle = !!(current?.youtube_id || current?.title_song_url);
  const jingleActive = watching || playing;
  const jingle = () => {
    if (watching) return stopWatch();
    if (playing) return stopSong();
    if (current?.youtube_id) return watchOnTv();
    if (current?.title_song_url) return playTitleSong();
    toast("No jingle for this show yet");
  };

  const playTitleSong = () => {
    if (!current?.title_song_url) { toast("No title song for this show"); return; }
    const a = audioRef.current;
    if (!a) return;
    const src = `${process.env.REACT_APP_BACKEND_URL}${current.title_song_url}`;
    if (a.src !== src) a.src = src;
    a.volume = volume;
    a.muted = muted;
    a.play().then(() => { setPlaying(true); ambient.duck(true); }).catch(() => toast.error("Couldn't play audio"));
  };

  const changeVolume = (delta) => {
    const v = Math.max(0, Math.min(1, +(volume + delta).toFixed(2)));
    setVolume(v);
    if (audioRef.current) audioRef.current.volume = v;
    if (v > 0 && muted) { setMuted(false); if (audioRef.current) audioRef.current.muted = false; }
  };
  const toggleMute = () => {
    const m = !muted; setMuted(m);
    if (audioRef.current) audioRef.current.muted = m;
  };

  return (
    <div className="min-h-screen bg-background paper-grain">
      <AmbientMusic src="/audio/nostalgic.mp3" />
      <audio
        ref={audioRef}
        onTimeUpdate={(e) => setProgress(e.target.duration ? (e.target.currentTime / e.target.duration) * 100 : 0)}
        onEnded={stopSong}
      />

      <header className="sticky top-0 z-40 border-b-[3px] border-kirana-brown bg-[#2b2f36] text-kirana-cream">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-3 px-4 py-2.5">
          <div>
            <h1 className="font-signboard text-2xl font-bold leading-none sm:text-3xl">Living Room · दूरदर्शन</h1>
            <p className="text-[11px] text-kirana-cream/80">Turn on the TV · flip through your childhood shows</p>
          </div>
          <div className="flex items-center gap-2">
            <div data-testid="tv-hud-count" className="hidden items-center gap-1.5 rounded-md border-2 border-kirana-cream bg-black/25 px-3 py-1.5 text-sm font-bold sm:flex">
              <Heart className="h-4 w-4 fill-kirana-cream" /> {memories.length} / {shows.length || 34}
            </div>
            <button data-testid="open-tv-memories" onClick={() => setCollection(true)}
              className="flex items-center gap-1.5 rounded-md border-2 border-kirana-brown bg-kirana-gold px-3 py-1.5 text-sm font-bold text-kirana-brown transition-transform hover:scale-105 hard-shadow-sm">
              <Heart className="h-4 w-4" /> <span className="hidden sm:inline">My TV Memories</span>
            </button>
            <Link data-testid="open-home" to="/" className="flex items-center gap-1.5 rounded-md border-2 border-kirana-cream px-3 py-1.5 text-sm font-bold transition-colors hover:bg-black/20">
              <HomeIcon className="h-4 w-4" /> <span className="hidden sm:inline">Home</span>
            </Link>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-3 py-4">
        <div
          data-testid="tv-room"
          className="relative overflow-hidden rounded-xl border-[4px] border-kirana-brown hard-shadow"
          style={{ backgroundImage: "url('/tv/living_room.jpg')", backgroundSize: "cover", backgroundPosition: "center" }}
        >
          <div className="absolute inset-0 bg-black/20" />

          <div className="relative flex flex-col items-center gap-4 px-2 py-4 sm:px-3 sm:py-8">
            {/* ---------- CRT TELEVISION ---------- */}
            <div className="relative w-full max-w-xl">
              {/* wooden TV body */}
              <div className="relative rounded-[22px] border-[4px] border-[#3a2a1c] bg-gradient-to-b from-[#6b4a2f] to-[#4a3120] p-4 sm:p-5 hard-shadow">
                <div className="flex flex-col gap-3 sm:flex-row">
                  {/* screen bezel */}
                  <div className="relative flex-1">
                    <div className="relative overflow-hidden rounded-[18px] border-[10px] border-[#22201d] bg-black crt-glow"
                      style={{ aspectRatio: "4 / 3" }}>
                      <div className={`absolute inset-0 crt-scanlines ${power ? "crt-flicker" : ""}`}>
                        {/* OFF */}
                        {!power && !turningOn && (
                          <div data-testid="tv-screen-off" className="flex h-full w-full items-center justify-center bg-[#0b0d0f]">
                            <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-white/5 via-transparent to-white/5" />
                            <span className="font-signboard text-sm text-white/30">— press POWER —</span>
                          </div>
                        )}

                        {/* TURNING ON */}
                        {turningOn && <div className="h-full w-full tv-static crt-power-on" />}

                        {/* ON: channel select */}
                        {power && !turningOn && !category && (
                          <div data-testid="tv-channel-menu" className="crt-power-on relative h-full w-full overflow-y-auto scrollbar-thin bg-[#0a1a2a] p-2 text-center sm:p-3">
                            <div className="mb-1.5 font-signboard text-xs font-bold tracking-widest text-[#7dd3fc] sm:mb-2 sm:text-2xl">SELECT CHANNEL</div>
                            <div className="grid grid-cols-2 gap-1.5 sm:gap-2">
                              {CATS.map((c, i) => (
                                <button key={c.id} data-testid={`tv-channel-${c.id}`} onClick={() => pickCategory(c.id)}
                                  className="flex min-h-0 flex-col justify-center rounded border-2 border-[#7dd3fc]/50 bg-black/40 px-2 py-1.5 text-left transition-colors hover:bg-[#7dd3fc]/15">
                                  <div className="font-signboard text-[13px] font-bold leading-tight text-[#e0f2fe] sm:text-lg">CH {i + 1} · {c.label}</div>
                                  <div className="text-[9px] leading-tight text-[#7dd3fc]/80 sm:text-[10px]">{c.sub}</div>
                                </button>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* ON: poster / video */}
                        {power && !turningOn && category && current && (
                          <div className="relative h-full w-full bg-black">
                            {watching && current.youtube_id ? (
                              <iframe
                                data-testid="tv-youtube"
                                title={current.name}
                                src={`https://www.youtube-nocookie.com/embed/${current.youtube_id}?autoplay=1&rel=0&playsinline=1`}
                                className="h-full w-full"
                                allow="autoplay; encrypted-media; fullscreen; picture-in-picture"
                                frameBorder="0"
                              />
                            ) : (
                              <>
                                <img
                                  data-testid="tv-poster"
                                  src={`${process.env.REACT_APP_BACKEND_URL}${current.poster_url}`}
                                  alt={current.name}
                                  className="h-full w-full cursor-pointer object-contain"
                                  onClick={jingle}
                                />
                                <button
                                  data-testid="tv-poster-info"
                                  onClick={(e) => { e.stopPropagation(); setCardOpen(true); }}
                                  className="absolute bottom-1.5 right-1.5 z-[9] flex h-7 w-7 items-center justify-center rounded-full border-2 border-[#7dd3fc] bg-black/70 text-[#e0f2fe] transition-transform hover:scale-110"
                                  title="Show details"
                                >
                                  <span className="font-signboard text-sm font-bold">i</span>
                                </button>
                                {!jingleActive && (
                                  <div className="pointer-events-none absolute left-1/2 top-1.5 -translate-x-1/2 rounded bg-black/60 px-2 py-0.5 text-[10px] font-bold tracking-wider text-[#7dd3fc]">
                                    ▶ TAP TO PLAY
                                  </div>
                                )}
                              </>
                            )}
                            {playing && !watching && (
                              <div className="absolute bottom-1.5 left-1/2 -translate-x-1/2 rounded bg-black/60 px-2 py-0.5 text-[10px] font-bold tracking-wider text-[#7dd3fc]">
                                ♪ PLAYING TITLE SONG
                              </div>
                            )}
                            {watching && (
                              <div className="absolute right-1.5 top-1.5 z-[9] flex items-center gap-1 rounded bg-black/70 px-1.5 py-0.5 text-[9px] font-bold tracking-wider text-[#ff4d4d]">
                                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[#ff4d4d]" /> ON AIR
                              </div>
                            )}
                          </div>
                        )}

                        {/* static overlay during channel change */}
                        {staticOn && !turningOn && <div className="absolute inset-0 z-[7] tv-static opacity-90" />}
                        {/* screen curvature vignette */}
                        <div className="pointer-events-none absolute inset-0 z-[8] rounded-[10px]" style={{ boxShadow: "inset 0 0 40px 8px rgba(0,0,0,0.55)" }} />
                      </div>
                    </div>
                  </div>

                  {/* control cluster — side column on desktop, wrapping row under the screen on mobile */}
                  <div className="flex w-full flex-row flex-wrap items-start justify-center gap-2 sm:w-16 sm:flex-col sm:items-center">
                    <div className="flex flex-col items-center">
                      <button data-testid="tv-power-btn" onClick={togglePower}
                        className={`flex h-9 w-9 items-center justify-center rounded-full border-2 border-black/50 ${power ? "bg-[#3fae5a]" : "bg-[#8b3a2a]"} text-white hard-shadow-sm transition-transform hover:scale-105`}>
                        <Power className="h-4 w-4" strokeWidth={3} />
                      </button>
                      <span className="mt-0.5 text-[8px] font-bold text-[#e9d8b8]">POWER</span>
                      <span className={`mt-1 h-2 w-2 rounded-full ${power ? "bg-green-400 shadow-[0_0_6px_#4ade80]" : "bg-red-500/70"}`} />
                    </div>

                    <PhysBtn tid="tv-ch-up" label="CH +" onClick={() => (power && category ? changeShow(1) : null)} icon={ChevronRight} />
                    <PhysBtn tid="tv-ch-down" label="CH −" onClick={() => (power && category ? changeShow(-1) : null)} icon={ChevronLeft} />
                    <PhysBtn tid="tv-side-song" label="JINGLE" onClick={() => (power && category ? jingle() : null)} icon={Youtube} />
                    <PhysBtn tid="tv-vol-up" label="VOL +" onClick={() => changeVolume(0.1)} icon={Volume2} />
                    <PhysBtn tid="tv-vol-down" label="VOL −" onClick={() => changeVolume(-0.1)} icon={VolumeX} />
                    <PhysBtn tid="tv-side-home" label="MENU" onClick={() => (power ? backToChannels() : powerOn())} icon={TvIcon} />
                  </div>
                </div>

                {/* speaker grille + brand */}
                <div className="mt-3 flex items-center justify-between">
                  <span className="font-signboard text-xs font-bold tracking-widest text-[#e9d8b8]">◄ NOSTALGIA COLOR ►</span>
                  <div className="flex gap-1">
                    {Array.from({ length: 10 }).map((_, i) => <span key={i} className="h-4 w-1 rounded bg-black/40" />)}
                  </div>
                </div>
              </div>
              {/* TV stand */}
              <div className="mx-auto h-3 w-2/3 rounded-b-lg border-x-4 border-b-4 border-[#3a2a1c] bg-[#4a3120]" />
            </div>

            {/* ---------- CONTROLS (below TV) ---------- */}
            {power && category && current && (
              <div data-testid="tv-controls" className="w-full max-w-xl rounded-lg border-[3px] border-kirana-brown bg-kirana-cream/95 p-3 hard-shadow-sm">
                <div className="flex flex-wrap items-center justify-center gap-2">
                  <CtrlBtn tid="tv-prev-show" onClick={() => changeShow(-1)} icon={SkipBack}>PREVIOUS</CtrlBtn>
                  <CtrlBtn tid="tv-change-show" onClick={() => changeShow(1)} icon={SkipForward} primary>CHANGE SHOW</CtrlBtn>
                  {hasJingle ? (
                    <CtrlBtn tid={jingleActive ? "tv-stop-song" : "tv-title-song"} onClick={jingle} icon={jingleActive ? Square : Youtube} accent>
                      {jingleActive ? "STOP" : "JINGLE"}
                    </CtrlBtn>
                  ) : (
                    <span className="rounded-md border-2 border-dashed border-kirana-border px-3 py-2 text-xs text-kirana-brown/50">No jingle</span>
                  )}
                  <CtrlBtn tid="tv-back-channels" onClick={backToChannels} icon={TvIcon}>CHANNELS</CtrlBtn>
                </div>

                {/* now-playing bar */}
                {playing && (
                  <div className="mt-3 flex items-center gap-2 rounded-md border-2 border-kirana-brown bg-white px-2 py-1.5">
                    <Music className="h-4 w-4 shrink-0 text-kirana-red" />
                    <div className="h-2 flex-1 overflow-hidden rounded-full bg-kirana-cream">
                      <div data-testid="tv-song-progress" className="h-full bg-kirana-red transition-all" style={{ width: `${progress}%` }} />
                    </div>
                    <button data-testid="tv-mute" onClick={toggleMute} className="text-kirana-brown">
                      {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
                    </button>
                    <span className="w-8 text-right text-[10px] font-bold text-kirana-brown">{Math.round(volume * 100)}</span>
                  </div>
                )}

                {/* show info */}
                <div className="mt-3 text-center">
                  <h2 data-testid="tv-show-name" className="font-signboard text-2xl font-bold leading-none text-kirana-brown">{current.name}</h2>
                  <div className="mt-1 flex flex-wrap items-center justify-center gap-1.5">
                    <span className="rounded border-2 border-kirana-brown bg-kirana-green px-2 py-0.5 text-[11px] font-bold text-white">{current.era}</span>
                    <span className="rounded border-2 border-kirana-brown bg-kirana-cream px-2 py-0.5 text-[11px] font-bold text-kirana-brown">{catLabel(current.category)}</span>
                    <span className="text-[11px] font-bold text-kirana-brown/60">{index + 1} / {list.length}</span>
                  </div>
                  <p className="mx-auto mt-2 max-w-md text-sm italic text-kirana-brown/80">"{current.memory}"</p>
                </div>
              </div>
            )}

            {/* ---------- BACK TO MAIN SCREEN ---------- */}
            <button data-testid="tv-back-hub" onClick={() => navigate("/")}
              className="flex items-center gap-2 rounded-md border-[3px] border-kirana-brown bg-kirana-brown px-5 py-2.5 font-bold text-kirana-cream transition-transform hover:scale-[1.04] hard-shadow">
              <HomeIcon className="h-5 w-5" /> Back to Nostalgic World
            </button>
          </div>
        </div>
      </main>

      {/* ---------- SHOW DETAIL CARD ---------- */}
      {cardOpen && current && (
        <Modal open={cardOpen} onClose={() => setCardOpen(false)} testId="tv-show-card" maxWidth="max-w-md">
          <div className="p-5 sm:p-7">
            <div className="mb-3 flex h-56 items-center justify-center overflow-hidden rounded-md border-[3px] border-kirana-brown bg-black crt-scanlines">
              <img src={`${process.env.REACT_APP_BACKEND_URL}${current.poster_url}`} alt={current.name} className="h-full w-full object-contain" />
            </div>
            <h2 className="font-signboard text-3xl font-bold leading-none text-kirana-brown">{current.name}</h2>
            <div className="mt-2 flex flex-wrap gap-2">
              <span className="rounded border-2 border-kirana-brown bg-kirana-green px-2 py-0.5 text-xs font-bold text-white">{current.era}</span>
              <span className="rounded border-2 border-kirana-brown bg-kirana-cream px-2 py-0.5 text-xs font-bold text-kirana-brown">{catLabel(current.category)}</span>
              {current.tags?.map((t) => <span key={t} className="rounded border-2 border-kirana-border bg-kirana-cream px-2 py-0.5 text-xs font-semibold text-kirana-brown/80">{t}</span>)}
            </div>
            {current.description && <p className="mt-3 text-sm text-kirana-brown">{current.description}</p>}
            <p className="mt-2 rounded-md border-2 border-dashed border-kirana-border bg-[repeating-linear-gradient(#FDF6E3,#FDF6E3_22px,#cdd4e6_23px)] p-3 text-sm italic text-kirana-brown">"{current.memory}"</p>
            {hasJingle && (
              <div className="mt-4 flex justify-center">
                <button data-testid={jingleActive ? "tv-card-stop" : "tv-card-play"} onClick={() => { setCardOpen(false); jingle(); }}
                  className="flex items-center gap-2 rounded-md border-[3px] border-kirana-brown bg-kirana-red px-5 py-2.5 font-bold text-white transition-transform hover:scale-[1.03] hard-shadow">
                  {jingleActive ? <Square className="h-5 w-5" /> : <Youtube className="h-5 w-5" />} {jingleActive ? "STOP" : "JINGLE"}
                </button>
              </div>
            )}
          </div>
        </Modal>
      )}

      {/* ---------- MY TV MEMORIES ---------- */}
      {collection && (
        <Modal open={collection} onClose={() => setCollection(false)} testId="tv-collection-panel" maxWidth="max-w-4xl">
          <div className="p-5 sm:p-7">
            <h2 className="font-signboard flex items-center gap-2 text-3xl font-bold text-kirana-brown">
              <Heart className="h-6 w-6 fill-kirana-red text-kirana-red" /> My TV Memories
            </h2>
            <div className="mt-3" data-testid="tv-progress">
              <div className="mb-1 flex justify-between text-xs font-bold text-kirana-brown"><span>Discovered</span><span>{memories.length} / {shows.length}</span></div>
              <div className="h-2.5 overflow-hidden rounded-full border-2 border-kirana-brown bg-kirana-cream">
                <div className="h-full bg-kirana-red transition-all" style={{ width: `${shows.length ? (memories.length / shows.length) * 100 : 0}%` }} />
              </div>
            </div>
            <div className="mt-4 grid max-h-[52vh] grid-cols-3 gap-3 overflow-y-auto scrollbar-thin pr-1 sm:grid-cols-4 lg:grid-cols-5">
              {shows.map((s) => {
                const u = memories.includes(s.id);
                return (
                  <div key={s.id} data-testid={`tv-mem-${s.id}`} className={`relative overflow-hidden rounded-md border-[3px] border-kirana-brown ${u ? "" : "opacity-45 grayscale"}`}>
                    <div className="flex h-24 items-center justify-center bg-black">
                      <img src={`${process.env.REACT_APP_BACKEND_URL}${s.poster_url}`} alt={s.name} className="h-full w-full object-contain" />
                    </div>
                    <div className="bg-card px-1.5 py-1"><span className="line-clamp-1 text-[11px] font-bold text-kirana-brown">{s.name}</span></div>
                    {u && <span className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full border-2 border-kirana-brown bg-kirana-cream"><Heart className="h-3 w-3 fill-kirana-red text-kirana-red" /></span>}
                  </div>
                );
              })}
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

const PhysBtn = ({ tid, label, onClick, icon: Icon }) => (
  <div className="flex flex-col items-center">
    <button data-testid={tid} onClick={onClick}
      className="flex h-7 w-7 items-center justify-center rounded border-2 border-black/50 bg-[#e9d8b8] text-[#3a2a1c] transition-transform hover:scale-105">
      <Icon className="h-3.5 w-3.5" strokeWidth={3} />
    </button>
    <span className="mt-0.5 text-[8px] font-bold text-[#e9d8b8]">{label}</span>
  </div>
);

const CtrlBtn = ({ tid, onClick, icon: Icon, children, primary, accent }) => (
  <button data-testid={tid} onClick={onClick}
    className={`flex items-center gap-1.5 rounded-md border-[3px] border-kirana-brown px-3 py-2 text-sm font-bold transition-transform hover:scale-[1.04] hard-shadow-sm ${
      primary ? "bg-kirana-gold text-kirana-brown" : accent ? "bg-kirana-red text-white" : "bg-kirana-cream text-kirana-brown"
    }`}>
    <Icon className="h-4 w-4" /> {children}
  </button>
);
